import {
  computeExciseBreakdownForLegs,
  computeQ4ComparisonTotalsForLegs,
} from "./dunleavyQ4ComparisonCompute";
import {
  DUNLEAVY_LOGBOOK_Q4_EXPECTED,
  DUNLEAVY_Q4_COMPARISON_RATES,
  formatComparisonUsd,
  mergeWorksheetFuelCharterOnly,
  mergeWorksheetWithEmailBenchmarkRates,
  mergeWorksheetWithWorksheetDefaultBenchmarkRates,
  Q1_2026_COMPARISON_REFERENCE,
  resolveEmailBenchmarkPassengerRates,
  resolveWorksheetDefaultDomesticSegmentUsd,
} from "./dunleavyQ4ComparisonReference";
import { dunleavyQ4LogbookDomesticBreakdownPanelHtml } from "./dunleavyQ4LogbookDomesticBreakdownPanel";
import { renderQ1AkDomesticLegDetailHtml } from "./dunleavyQ1AkDomesticDetailHtml";
import {
  filterQ1_2026TimeShareLegsFromLogbook,
  filterQ4_2025TimeShareLegsFromLogbook,
} from "./dunleavyQ4LogbookLegs";
import { money } from "./money";
import type { FlightLeg, Rates } from "./types";

function rowTrio(
  label: string,
  emailExcerpt: number,
  supabase480: number,
  supabase530: number,
  note: string
): string {
  const eq = (a: number, b: number) => money(a) === money(b);
  const allMatch =
    eq(emailExcerpt, supabase480) && eq(supabase480, supabase530);
  const cls = allMatch ? "comparison-match" : "comparison-diff";
  return `<tr class="${cls}">
    <td>${label}</td>
    <td class="comparison-num">${formatComparisonUsd(emailExcerpt)}</td>
    <td class="comparison-num">${formatComparisonUsd(supabase480)}</td>
    <td class="comparison-num">${formatComparisonUsd(supabase530)}</td>
    <td class="comparison-note muted-small">${note}</td>
  </tr>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function q1RefComparisonRow(
  label: string,
  refAmount: number,
  liveAmount: number,
  note: string
): string {
  const match = money(refAmount) === money(liveAmount);
  return `<tr class="${match ? "comparison-match" : "comparison-diff"}">
    <td>${escapeHtml(label)}</td>
    <td class="comparison-num">${formatComparisonUsd(refAmount)}</td>
    <td class="comparison-num">${formatComparisonUsd(liveAmount)}</td>
    <td class="comparison-note muted-small">${escapeHtml(note)}</td>
  </tr>`;
}

function renderQ1_2026ComparisonBlock(
  allLegs: FlightLeg[],
  worksheetRates: Rates
): string {
  const ref = Q1_2026_COMPARISON_REFERENCE;
  const legs = filterQ1_2026TimeShareLegsFromLogbook(allLegs);
  const rates = mergeWorksheetWithWorksheetDefaultBenchmarkRates(worksheetRates);
  const exc = computeExciseBreakdownForLegs(legs, rates);
  const totals = computeQ4ComparisonTotalsForLegs(legs, rates);
  /** Same economics as reference “7.5% charter” line: IRS charter % plus optional Form 720 % on 2× fuel (mutually complementary in the app). */
  const wsPctTransportationTotal = money(
    exc.irs26PercentageTax + totals.optionalPersonalPctTax
  );
  const scopeNote =
    legs.length === 0
      ? "No 2026 Q1 <code>[Time share]</code> legs in the worksheet — worksheet column is $0.00."
      : `Worksheet column uses <strong>${legs.length}</strong> filtered leg(s), built-in <strong>$5.30 / $23.40 / $11.70</strong> passenger lines, and your Form 720 fuel + charter %.`;
  return `
    <section class="panel">
      <h3 class="agreements-pane-title">${escapeHtml(ref.periodLabel)} — comparison reference</h3>
      <p class="sheets-hint muted-small">Manual Q1 2026 package vs recomputed legs. The reference labels the <strong>$1,592.54</strong> line as 7.5%×charter; if <code>charter_usd</code> is blank on legs, the worksheet puts the same style of 7.5% on <strong>optional personal % × 2× fuel</strong> (Form 720 tab) instead — the <strong>7.5% transportation</strong> row adds those so it matches the reference charter line.</p>
      <p class="sheets-hint muted-small">${scopeNote}</p>
      <div class="comparison-table-wrap">
        <table class="totals comparison-table">
          <thead>
            <tr>
              <th scope="col">Line</th>
              <th scope="col">Reference</th>
              <th scope="col">Worksheet</th>
              <th scope="col">Note</th>
            </tr>
          </thead>
          <tbody>
            ${q1RefComparisonRow(
              "2× fuel (reimbursement)",
              ref.twoXFuel,
              totals.twoXFuel,
              "Form 720 fuel lbs/gal + $/gal on filtered legs."
            )}
            ${q1RefComparisonRow(
              "7.5% transportation (charter $ + optional 2× fuel)",
              ref.charterPctTax,
              wsPctTransportationTotal,
              "Worksheet = IRS 7.5%×charter $ + optional 7.5%×2× fuel (personal %). Reference = charter-only manual line."
            )}
            ${q1RefComparisonRow(
              "AK/HI departure tax",
              ref.alaskaHawaiiDepartureTax,
              exc.irs26AkHiDepartureTax,
              ref.notes.akHi
            )}
            ${q1RefComparisonRow(
              "IRS No. 26 — domestic segment",
              ref.domesticSegmentTax,
              exc.irs26DomesticSegmentTax,
              ref.notes.domestic
            )}
            ${q1RefComparisonRow(
              "IRS No. 27 — international facility",
              ref.internationalFacilityTax,
              exc.irs27InternationalFacilityTax,
              ref.notes.international
            )}
            ${q1RefComparisonRow(
              "Owed to Utah Gas Corp (package)",
              ref.owedUtahGasCorp,
              totals.grandTotal,
              "Agreement-style total: 2× fuel + all leg air excise (7.5% / domestic / intl / AK) + optional %. Not the same as IRS Form 720 tax due — see note below."
            )}
          </tbody>
        </table>
      </div>
      ${renderQ1AkDomesticLegDetailHtml(legs, rates)}
      <p class="sheets-hint muted-small"><strong>Form 720 (IRS)</strong> in this app is <strong>federal air excise only</strong> — draft PDF puts amounts on IRS Nos. <strong>26</strong> (percentage + domestic segment + optional personal % on 2× fuel when enabled) and <strong>27</strong> (international facility + AK/HI departure). Per instructions, <strong>Alaska/Hawaii → contiguous <code>K…</code></strong> (e.g. Anchorage→Seattle) carries <strong>both</strong> domestic segment on <strong>No. 26</strong> and AK/HI departure on <strong>No. 27</strong> on the same leg. It does <strong>not</strong> include <strong>2× fuel reimbursement</strong>; that stays in the Utah Gas row above.</p>
      <p class="sheets-hint muted-small">If <strong>AK</strong> or <strong>domestic segment</strong> still differ, open the per-leg detail above; the reference often uses shorthand pax×leg counts while the worksheet follows each leg’s <code>dom_pax</code>, <code>passenger_count</code>, <code>segments_domestic</code>, and Alaska/Hawaii <strong>departure-from-PA/PH</strong> rule.</p>
    </section>
  `;
}

const emailFixed = DUNLEAVY_Q4_COMPARISON_RATES;

/**
 * Comparison tab: Q4 2025 `[Time share]` legs — fixed email benchmark vs built-in $4.80-era vs built-in worksheet-default ($5.30) passenger rates.
 */
export function renderDunleavyComparisonPanelHtml(
  allLegs: FlightLeg[],
  err: string | null,
  worksheetRates: Rates
): string {
  const filteredLegs = filterQ4_2025TimeShareLegsFromLogbook(allLegs);
  const rEmail = resolveEmailBenchmarkPassengerRates();
  const ratesEmailExcerpt = mergeWorksheetFuelCharterOnly(
    worksheetRates,
    emailFixed
  );
  const lEmailExcerpt = computeQ4ComparisonTotalsForLegs(
    filteredLegs,
    ratesEmailExcerpt
  );
  const lSupa480 = computeQ4ComparisonTotalsForLegs(
    filteredLegs,
    mergeWorksheetWithEmailBenchmarkRates(worksheetRates)
  );
  const lSupa530 = computeQ4ComparisonTotalsForLegs(
    filteredLegs,
    mergeWorksheetWithWorksheetDefaultBenchmarkRates(worksheetRates)
  );
  const w530Passenger = mergeWorksheetWithWorksheetDefaultBenchmarkRates(worksheetRates);
  const worksheetDefaultDomesticUsd = resolveWorksheetDefaultDomesticSegmentUsd();
  const refCsv = DUNLEAVY_LOGBOOK_Q4_EXPECTED;
  const matchesBundledCsv =
    lEmailExcerpt.legCount === refCsv.legCount &&
    money(lEmailExcerpt.domesticSegment) === money(refCsv.domesticSegment) &&
    money(lEmailExcerpt.internationalFacility) ===
      money(refCsv.internationalFacility) &&
    money(lEmailExcerpt.combinedAirExcise) === money(refCsv.combinedAirExcise) &&
    money(lEmailExcerpt.twoXFuel) === money(refCsv.twoXFuel) &&
    money(lEmailExcerpt.optionalPersonalPctTax) ===
      money(refCsv.optionalPersonalPctTax) &&
    money(lEmailExcerpt.grandTotal) === money(refCsv.grandTotal);

  const errBlock =
    err && err.length > 0
      ? `<p class="error" role="alert"><strong>Worksheet parse error:</strong> ${escapeHtml(err)} — totals below may use last good legs or empty.</p>`
      : "";

  const liveNote =
    filteredLegs.length === 0
      ? "No Q4 2025 <code>[Time share]</code> legs in the current worksheet."
      : `From <strong>${filteredLegs.length}</strong> filtered leg(s); same merge as Form 720 (cloud + CSV + manual).`;

  const parityNote = matchesBundledCsv
    ? ` Matches repo reference totals (same arithmetic as Vitest fixture <code>dunleavyQ4_2025_timeshare.csv</code>; column 1 = email excerpt + your Form 720 fuel / charter %).`
    : ` Differs from repo reference totals (expected when legs, fuel, or charter % changed vs the fixture).`;

  return `
    ${errBlock}
    <div class="comparison-tab-intro">
      <p class="sheets-hint muted-small">
        <strong>Scope:</strong> Oct–Dec 2025 legs with <code>[Time share]</code> in route notes — same cloud + CSV + manual merge as Form 720.
      </p>
      <div class="comparison-rate-grid comparison-rate-grid--triple" role="group" aria-label="Passenger tax rates used in the three totals columns">
        <div class="comparison-rate-col">
          <p class="comparison-rate-heading">Column 1 — Email excerpt (fixed)</p>
          <ul class="comparison-rate-list">
            <li>Domestic: <strong>$${emailFixed.domesticSegmentUsdPerPassengerPerSegment.toFixed(2)}</strong>/pax/segment</li>
            <li>Intl: <strong>$${emailFixed.internationalFacilityUsdPerPerson.toFixed(2)}</strong>/person</li>
            <li>AK/HI: <strong>$${emailFixed.alaskaHawaiiDomesticDepartureUsdPerPerson.toFixed(2)}</strong>/person</li>
            <li>Fuel lbs/gal + charter % from <strong>Form 720</strong> (not Pilot costs).</li>
            <li class="muted-small">December email benchmark; fixed reference vs live worksheet.</li>
          </ul>
        </div>
        <div class="comparison-rate-col">
          <p class="comparison-rate-heading">Column 2 — $4.80-era (built-in)</p>
          <ul class="comparison-rate-list">
            <li>Domestic: <strong>$${rEmail.domesticSegmentUsdPerPassengerPerSegment.toFixed(2)}</strong>/pax/segment</li>
            <li>Intl: <strong>$${rEmail.internationalFacilityUsdPerPerson.toFixed(2)}</strong>/person</li>
            <li>AK/HI: <strong>$${rEmail.alaskaHawaiiDomesticDepartureUsdPerPerson.toFixed(2)}</strong>/person</li>
            <li>Fuel lbs/gal + charter % from Form 720</li>
            <li class="muted-small">Fixed <strong>$4.80 / $21.10 / $11.70</strong> (same passenger math as column 1; column 1 uses the email-rate merge path).</li>
          </ul>
        </div>
        <div class="comparison-rate-col">
          <p class="comparison-rate-heading">Column 3 — Worksheet default (built-in)</p>
          <ul class="comparison-rate-list">
            <li>Domestic: <strong>$${w530Passenger.domesticSegmentUsdPerPassengerPerSegment.toFixed(2)}</strong>/pax/segment</li>
            <li>Intl: <strong>$${w530Passenger.internationalFacilityUsdPerPerson.toFixed(2)}</strong>/person</li>
            <li>AK/HI: <strong>$${w530Passenger.alaskaHawaiiDomesticDepartureUsdPerPerson.toFixed(2)}</strong>/person</li>
            <li>Fuel lbs/gal + charter % from Form 720</li>
            <li class="muted-small">Fixed <strong>$5.30 / $23.40 / $11.70</strong> (Form 720 tab default passenger lines).</li>
          </ul>
        </div>
      </div>
      <ul class="comparison-tab-rules">
        <li><strong>Passengers:</strong> <code>dom_pax</code> if set; else <code>passenger_count</code> (name list is not used for domestic segment tax).</li>
        <li><strong>Foreign–foreign</strong> (neither end US <code>K…</code>/<code>PA…</code>/<code>PH…</code>): 0 domestic segment pax (<code>passenger_count</code> ignored) unless <code>dom_pax</code> is set.</li>
        <li><strong>No. 27:</strong> <code>intl_pax</code> counts only on <strong>US ↔ non-US</strong> routes.</li>
      </ul>
      <p class="sheets-hint muted-small comparison-tab-foot">
        <strong>Totals table:</strong> columns <strong>1–3</strong> use the passenger rates above (1 = email excerpt path; 2–3 = built-in benchmarks). <strong>Row-by-row</strong> uses Form 720 domestic + <strong>$${worksheetDefaultDomesticUsd.toFixed(2)}</strong> alternate domestic (worksheet default).${parityNote}
      </p>
    </div>

    <section class="panel">
      <h3 class="agreements-pane-title">Totals</h3>
      <p class="sheets-hint muted-small">${liveNote}</p>
      <div class="comparison-table-wrap">
        <table class="totals comparison-table">
          <thead>
            <tr>
              <th scope="col">Line</th>
              <th scope="col" title="Fixed email rates ($4.80 / $21.10 / $11.70); fuel &amp; charter % from Form 720">Email excerpt<br /><span class="muted-small">fixed · $${emailFixed.domesticSegmentUsdPerPassengerPerSegment.toFixed(2)} dom</span></th>
              <th scope="col" title="Built-in $4.80-era passenger lines; fuel &amp; charter % from Form 720">$4.80-era<br /><span class="muted-small">$${rEmail.domesticSegmentUsdPerPassengerPerSegment.toFixed(2)} dom</span></th>
              <th scope="col" title="Built-in worksheet-default passenger lines ($5.30 / $23.40 / $11.70); fuel &amp; charter % from Form 720">Worksheet default<br /><span class="muted-small">$${w530Passenger.domesticSegmentUsdPerPassengerPerSegment.toFixed(2)} dom</span></th>
              <th scope="col">Note</th>
            </tr>
          </thead>
          <tbody>
            <tr class="comparison-match">
              <td>Legs in scope</td>
              <td class="comparison-num">${lEmailExcerpt.legCount}</td>
              <td class="comparison-num">${lSupa480.legCount}</td>
              <td class="comparison-num">${lSupa530.legCount}</td>
              <td class="comparison-note muted-small">Q4 2025 <code>[Time share]</code> filter.</td>
            </tr>
            ${rowTrio(
              "IRS No. 26 — domestic segment",
              lEmailExcerpt.domesticSegment,
              lSupa480.domesticSegment,
              lSupa530.domesticSegment,
              "Segment-units × rate; foreign–foreign excluded unless dom_pax set."
            )}
            ${rowTrio(
              "IRS No. 27 — international facility",
              lEmailExcerpt.internationalFacility,
              lSupa480.internationalFacility,
              lSupa530.internationalFacility,
              "Effective passengers × rate (US↔non-US only)."
            )}
            ${rowTrio(
              "Combined air excise (26 + 27 …)",
              lEmailExcerpt.combinedAirExcise,
              lSupa480.combinedAirExcise,
              lSupa530.combinedAirExcise,
              "Includes charter % (Form 720 tab) + passenger lines."
            )}
            ${rowTrio(
              "2× fuel (reimbursement)",
              lEmailExcerpt.twoXFuel,
              lSupa480.twoXFuel,
              lSupa530.twoXFuel,
              "Identical all columns (uses Form 720 fuel lbs/gal)."
            )}
            ${rowTrio(
              "Optional % tax on 2× fuel (worksheet model)",
              lEmailExcerpt.optionalPersonalPctTax,
              lSupa480.optionalPersonalPctTax,
              lSupa530.optionalPersonalPctTax,
              "Identical (Form 720 tax % × 2× fuel)."
            )}
            ${rowTrio(
              "Grand total (2× fuel + air excise + optional %)",
              lEmailExcerpt.grandTotal,
              lSupa480.grandTotal,
              lSupa530.grandTotal,
              "Illustrative package total (not a filed Form 720 line)."
            )}
          </tbody>
        </table>
      </div>
      <p class="sheets-hint muted-small">
        Row-by-row <strong>domestic segment</strong> and <strong>No. 27 effective passengers</strong> are in the table below.
      </p>
    </section>

    ${dunleavyQ4LogbookDomesticBreakdownPanelHtml(
      filteredLegs,
      worksheetRates,
      worksheetDefaultDomesticUsd
    )}

    ${renderQ1_2026ComparisonBlock(allLegs, worksheetRates)}

    <section class="panel panel-tight">
      <h3 class="agreements-pane-title">Where to see these numbers live</h3>
      <p class="sheets-hint muted-small">Same legs as <button type="button" class="tab-jump-link" data-open-main-tab="f720">Form 720</button>. Timeshare reimbursement: <button type="button" class="tab-jump-link" data-open-main-tab="ugc">Timeshare</button>.</p>
    </section>

    <section class="panel panel-tight">
      <h3 class="agreements-pane-title">Automated tests</h3>
      <ul class="policy-list sheets-hint">
        <li><code>fourQuarterTimeshareN426dh.test.ts</code> — synthetic email fixture (unit tests).</li>
        <li><code>fourQuarterTimeshareN426dh.logbook.test.ts</code> — <code>fixtures/dunleavyQ4_2025_timeshare.csv</code> vs <code>DUNLEAVY_LOGBOOK_Q4_EXPECTED</code>.</li>
        <li>Reference totals: <code>dunleavyQ4ComparisonReference.ts</code>.</li>
      </ul>
    </section>
  `;
}

export function updateComparisonPanel(
  root: HTMLElement,
  legs: FlightLeg[],
  err: string | null,
  worksheetRates: Rates
): void {
  const el = root.querySelector<HTMLElement>("#comparison-panel-root");
  if (!el) return;
  el.innerHTML = renderDunleavyComparisonPanelHtml(legs, err, worksheetRates);
}
